/**
 * "Our Trail" — Journail's changelog with a nicer name. Single source of truth,
 * rendered by the public page (src/app/guides/our-trail/page.tsx).
 *
 * Entries are newest-first. Each change is tagged so the timeline can colour
 * New / Improved / Fixed consistently. Bodies use a tiny inline-markdown subset
 * (`**bold**`, `*italic*`, `` `code` ``) rendered by the page.
 */

export type TrailLang = "sl" | "en";
export type ChangeKind = "new" | "improved" | "fixed";

export interface ChangeItem {
  kind: ChangeKind;
  text: string;
}

export interface TrailEntry {
  version: string; // e.g. "1.2"
  /** ISO date for the <time> element. */
  date: string;
  /** Localized human date, e.g. "8. junij 2026" / "June 8, 2026". */
  dateLabel: string;
  title: string;
  intro?: string;
  changes: ChangeItem[];
}

export interface TrailCopy {
  eyebrow: string;
  title: { a: string; em: string };
  subtitle: string;
  latestLabel: string;
  kindLabels: Record<ChangeKind, string>;
  /** Newest first. */
  entries: TrailEntry[];
}

export const TRAIL_COPY: Record<TrailLang, TrailCopy> = {
  sl: {
    eyebrow: "VODNIKI · NAŠA SLED",
    title: { a: "Naša sled — ", em: "kje smo že bili" },
    subtitle:
      "Journail gradimo počasi in namerno. Tu je sled, ki smo jo pustili za sabo — verzijo za verzijo, kaj je prišlo in kaj se je izboljšalo.",
    latestLabel: "Najnovejše",
    kindLabels: { new: "Novo", improved: "Izboljšano", fixed: "Popravljeno" },
    entries: [
      {
        version: "1.3",
        date: "2026-06-30",
        dateLabel: "30. junij 2026",
        title: "Vsako opravilo najde svoj cilj",
        intro:
          "Journail zdaj sam poveže tvoja opravila z velikimi cilji — predlaga, ti potrdiš. Manj ročnega razvrščanja, več jasnosti, kam vodi tvoj dan.",
        changes: [
          { kind: "new", text: "**AI predlaga cilj za vsako opravilo.** Ko dodaš nalogo — v *AI pomočniku*, na nadzorni plošči ali v koledarju — Journail predlaga, h kateremu cilju sodi. Predlog le potrdiš ali spremeniš." },
          { kind: "new", text: "**Cilji že v jutranjem planu.** Med jutranjim briefom odprta opravila brez cilja dobijo AI predlog z izbirnikom za spremembo. Če predlog zavrneš, si Journail to zapomni in te ne sprašuje znova." },
          { kind: "improved", text: "**»Tvojih podatkov ne uporabljamo za učenje AI« — zdaj uveljavljeno v kodi.** Vsak AI klic se usmeri le k ponudnikom, ki tvoje vsebine ne zbirajo in ne učijo modelov." },
          { kind: "improved", text: "**Prenovljena domača stran** z živimi prikazi, kako Journail sklene tvoj dan." },
        ],
      },
      {
        version: "1.2",
        date: "2026-06-08",
        dateLabel: "8. junij 2026",
        title: "Odprto in zaupanja vredno",
        intro:
          "Verzija o tem, čigava je tvoja vsebina in kako hitro te Journail postreže. Spodaj odpremo vrata in pospravimo zaledje.",
        changes: [
          { kind: "new", text: "**Tvoji podatki, tvoji.** Izvoz vseh podatkov v JSON in samopostrežni izbris računa v *Nastavitve → Zasebnost*, skupaj z upravljanjem soglasij in piškotkov." },
          { kind: "new", text: "**Admin ne bere vsebine.** Administrativne površine vidijo le agregate in identiteto — nikoli tvojih dnevnikov, ciljev ali pogovorov. Vsak dostop do osebnih podatkov je revidiran." },
          { kind: "new", text: "Nova razdelka **Navodila** in **Naša sled** (ta stran) v meniju Vodniki." },
          { kind: "improved", text: "**Hitrejša jutra.** Indeksi v bazi in vzporedno izvajanje jutranjih/večernih opravil občutno skrajšajo čakanje." },
          { kind: "improved", text: "Javni **FAQ**, **blog** in *sitemap* s strukturiranimi podatki — Journail je zdaj lažje najti." },
          { kind: "fixed", text: "Slovnično pravilna imena dni (»Sobota *je ostala odprta*«, »Zaključi *soboto*«) in dosledno lokaliziran jutranji tok." },
        ],
      },
      {
        version: "1.1",
        date: "2026-05-12",
        dateLabel: "12. maj 2026",
        title: "Poveži in se udomači",
        intro:
          "Journail se vklopi v orodja, ki jih že uporabljaš, in se prilagodi tvojemu ritmu.",
        changes: [
          { kind: "new", text: "**Google Calendar** — dogodki ob nalogah, ustvarjanje v naravnem jeziku, RSVP in Google Meet." },
          { kind: "new", text: "**Todoist** — tvoje naloge se zlijejo z internimi, brez podvajanja." },
          { kind: "new", text: "**Delovni čas in odsotnosti** — Journail ve, kdaj je tvoj dan, in temu prilagodi jutranji ter večerni opomnik." },
          { kind: "new", text: "**Uvodni panel** te ob prvem obisku pelje skozi nastavitve." },
          { kind: "improved", text: "**Svetla tema privzeto** in toplejši papir v temnem načinu — prijaznejše očem ob vsaki uri." },
          { kind: "improved", text: "Stranski pomočnik se preimenuje v **AI pomočnik** — eno ime povsod." },
        ],
      },
      {
        version: "1.0",
        date: "2026-04-14",
        dateLabel: "14. april 2026",
        title: "Prva oznaka na poti",
        intro:
          "Začetek. Cel dnevni krog — od jutranjega načrta do večerne refleksije — v enem mirnem prostoru.",
        changes: [
          { kind: "new", text: "**Jutranji brief** — v petih minutah do plana dneva: AI predlaga prioritete, ti izbereš naloge, plan potrdiš." },
          { kind: "new", text: "**Večerni pregled** — dan zaključiš s kratko refleksijo, ki se zapiše v dnevnik." },
          { kind: "new", text: "**Cilji** v treh ciklih — tedenski, mesečni, letni — povezani z nalogami." },
          { kind: "new", text: "**Arhiv dnevnika** z iskanjem, filtri in izvozom v Markdown." },
          { kind: "new", text: "**Prijava** z e-pošto ali Googlom; brezplačni preizkus prek naročnine." },
        ],
      },
    ],
  },
  en: {
    eyebrow: "GUIDES · OUR TRAIL",
    title: { a: "Our trail — ", em: "where we've been" },
    subtitle:
      "We build Journail slowly and on purpose. Here's the trail we've left behind — version by version, what arrived and what got better.",
    latestLabel: "Latest",
    kindLabels: { new: "New", improved: "Improved", fixed: "Fixed" },
    entries: [
      {
        version: "1.3",
        date: "2026-06-30",
        dateLabel: "June 30, 2026",
        title: "Every task finds its goal",
        intro:
          "Journail now ties your tasks to your bigger goals on its own — it suggests, you confirm. Less manual sorting, more clarity about where your day is heading.",
        changes: [
          { kind: "new", text: "**AI suggests a goal for every task.** Add a task — in the *AI Assistant*, on the dashboard, or in the calendar — and Journail proposes which goal it belongs to. Just confirm or change it." },
          { kind: "new", text: "**Goals right in the morning plan.** During the morning brief, open tasks without a goal get an AI suggestion with a dropdown to adjust. Reject one and Journail remembers — it won't ask again." },
          { kind: "improved", text: "**\"We never train AI on your data\" — now enforced in code.** Every AI request routes only to providers that don't collect or train on your content." },
          { kind: "improved", text: "**Refreshed homepage** with living demos of how Journail closes your day." },
        ],
      },
      {
        version: "1.2",
        date: "2026-06-08",
        dateLabel: "June 8, 2026",
        title: "Open and trustworthy",
        intro:
          "A release about whose content this is and how fast Journail serves it. We open the doors and tidy the backend.",
        changes: [
          { kind: "new", text: "**Your data, yours.** Export everything to JSON and delete your account yourself in *Settings → Privacy*, alongside consent and cookie controls." },
          { kind: "new", text: "**Admins don't read your content.** Admin surfaces see only aggregates and identity — never your journals, goals, or conversations. Every access to personal data is audited." },
          { kind: "new", text: "New **Tutorials** and **Our Trail** (this page) sections under the Guides menu." },
          { kind: "improved", text: "**Faster mornings.** Database indexes and parallelised morning/evening jobs cut the wait noticeably." },
          { kind: "improved", text: "Public **FAQ**, **blog**, and a *sitemap* with structured data — Journail is easier to find now." },
          { kind: "fixed", text: "Grammatically correct Slovenian day names and a fully localized morning flow." },
        ],
      },
      {
        version: "1.1",
        date: "2026-05-12",
        dateLabel: "May 12, 2026",
        title: "Connect and settle in",
        intro: "Journail plugs into the tools you already use and adapts to your rhythm.",
        changes: [
          { kind: "new", text: "**Google Calendar** — events next to your tasks, natural-language creation, RSVP, and Google Meet." },
          { kind: "new", text: "**Todoist** — your tasks merge with the internal ones, no duplication." },
          { kind: "new", text: "**Work hours and absences** — Journail knows when your day runs and times your morning and evening reminders to it." },
          { kind: "new", text: "An **onboarding panel** walks you through setup on first visit." },
          { kind: "improved", text: "**Light theme by default** and a warmer paper in dark mode — easier on the eyes at any hour." },
          { kind: "improved", text: "The side assistant is renamed the **AI Assistant** — one name everywhere." },
        ],
      },
      {
        version: "1.0",
        date: "2026-04-14",
        dateLabel: "April 14, 2026",
        title: "The first marker on the trail",
        intro:
          "The beginning. The full daily loop — from the morning plan to the evening reflection — in one calm place.",
        changes: [
          { kind: "new", text: "**Morning Brief** — a plan in five minutes: the AI suggests priorities, you pick tasks, you confirm the plan." },
          { kind: "new", text: "**Evening debrief** — close the day with a short reflection that's written into your journal." },
          { kind: "new", text: "**Goals** in three cycles — weekly, monthly, yearly — linked to your tasks." },
          { kind: "new", text: "**Journal archive** with search, filters, and Markdown export." },
          { kind: "new", text: "**Sign in** with email or Google; a free trial via subscription." },
        ],
      },
    ],
  },
};
